import { Navbar, NavbarBrand, NavbarCollapse, NavbarLink, NavbarToggle } from 'flowbite-react'
import React from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import type { HeaderMetaProps } from '../../data/types'
import { LogoIcon } from '../../icons'
import { useAuth } from '../../routes'
import { ThemeToggle } from '../ThemeToggle'
import { LanguageSelector } from '../LanguageSelector'
import { UserMenu } from '../UserMenu'

type Props = {
    meta: HeaderMetaProps,
    className?: string
}

export const Header = (props: Props) => {
    const { meta, className = '' } = props
    const location = useLocation()

    const { isAuthenticated, user, logout, loggingOut, incompletePoliciesCount } = useAuth()
    const navigate = useNavigate()

    const showProfileBadge = user?.volunteer && !user.volunteer.profile_submitted_at;
    const showPoliciesBadge = incompletePoliciesCount > 0

    return (
        <>
            <Navbar fluid className={clsx('border-b-2 border-primary-600', className)}>
                <NavbarBrand as={(props) => <Link to="/" {...props} />}>
                    <LogoIcon onClick={() => navigate(isAuthenticated ? '/' : '/login')} className="cursor-pointer"/>
                    <span className="self-center whitespace-nowrap text-lg font-semibold tracking-tighter dark:text-white ms-3">
                        {meta.title}
                    </span>
                </NavbarBrand>
                <div className="flex md:order-2 items-center gap-2">
                    {/* Desktop only - UserMenu and ThemeToggle */}
                    <div className="hidden md:flex items-center gap-2">
                        {isAuthenticated && <UserMenu />}
                        <ThemeToggle
                            lightLabel={meta.themeToggleLightLabel}
                            darkLabel={meta.themeToggleDarkLabel}
                            systemLabel={meta.themeToggleSystemLabel}
                        />
                        <LanguageSelector
                            label={meta.languageSelectorLabel}
                        />
                    </div>
                    <NavbarToggle/>
                </div>

                {isAuthenticated && (
                    <NavbarCollapse className="md:ms-auto md:me-4 [&>ul]:md:space-x-0 [&>ul]:md:gap-8">
                        {Object.entries(meta.nav).map(([k, v]) => {
                            if (v.show && !v.show(user)) {
                                return null
                            }
                            const isActive = location.pathname === v.path || location.pathname.startsWith(v.path + '/')
                            const isProfileLink = k === 'profile'
                            const isPoliciesLink = k === 'policies'
                            const isExternalLink = v.path.startsWith('http://') || v.path.startsWith('https://')

                            return (
                                <NavbarLink
                                    active={isActive}
                                    className={isActive ? 'md:text-primary-500 md:dark:text-primary-400 text-white border-none' : 'border-none'}
                                    as={(props) =>
                                        isExternalLink
                                            ? <a href={v.path} target="_blank" rel="noopener noreferrer" {...props} />
                                            : <Link to={v.path} {...props} />
                                    }
                                    key={k}
                                >
                                    <span className="flex items-center gap-2">
                                        {v.title}
                                        {isProfileLink && showProfileBadge && (
                                            <span className="inline-flex items-center justify-center w-2 h-2 bg-warn-500 rounded-full" title="Profile incomplete">
                                                <span className="sr-only">Profile incomplete</span>
                                            </span>
                                        )}
                                        {isPoliciesLink && showPoliciesBadge && (
                                            <span className="inline-flex items-center justify-center w-2 h-2 bg-danger-500 rounded-full" title="Incomplete policies">
                                                <span className="sr-only">Incomplete policies</span>
                                            </span>
                                        )}
                                    </span>
                                </NavbarLink>
                            )
                        })}

                        {/* Mobile only - Theme and User Menu */}
                        <div className="md:hidden mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 space-y-2">
                            <ThemeToggle
                                lightLabel={meta.themeToggleLightLabel}
                                darkLabel={meta.themeToggleDarkLabel}
                                systemLabel={meta.themeToggleSystemLabel}
                                showLabel={true}
                            />
                            <LanguageSelector
                                label={meta.languageSelectorLabel}
                                showLabel={true}
                            />
                            <UserMenu mobile={true} />
                        </div>
                    </NavbarCollapse>
                )}

                {!isAuthenticated && (
                    <NavbarCollapse>
                        <div className="md:hidden mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 space-y-2">
                            <ThemeToggle
                                lightLabel={meta.themeToggleLightLabel}
                                darkLabel={meta.themeToggleDarkLabel}
                                systemLabel={meta.themeToggleSystemLabel}
                                showLabel={true}
                            />
                            <LanguageSelector
                                label={meta.languageSelectorLabel}
                                showLabel={true}
                            />
                        </div>
                    </NavbarCollapse>
                )}
            </Navbar>
        </>
    )
}
